import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { Search, User, MapPin, Stethoscope, Mail, Phone, ExternalLink, X, Quote } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function ColleaguesPage() {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);

  async function load(query = "") {
    setLoading(true);
    try {
      const { data } = await api.get(`/doctors/directory${query ? `?q=${encodeURIComponent(query)}` : ""}`);
      setItems(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-8 fade-in-up" data-testid="colleagues-page">
      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-stone-500 mb-3">{t("colleagues.eyebrow")}</p>
        <h1 className="font-heading text-5xl lg:text-6xl font-light tracking-tight">{t("colleagues.title")}</h1>
        <p className="text-stone-500 mt-2 max-w-2xl">{t("colleagues.subtitle")}</p>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); load(q); }}
        className="flex items-center gap-2 bg-white border border-stone-200 rounded-full pl-5 pr-2 py-1 max-w-xl">
        <Search className="w-4 h-4 text-stone-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)}
          placeholder={t("colleagues.searchPh")} data-testid="colleagues-search"
          className="flex-1 bg-transparent px-2 py-2 focus:outline-none text-sm" />
        <button type="submit" data-testid="colleagues-search-btn"
          className="px-5 py-2 rounded-full bg-stone-900 text-white text-xs uppercase tracking-widest hover:bg-stone-700">
          {t("common.search") || "Cerca"}
        </button>
      </form>

      {loading ? (
        <p className="text-stone-400 text-sm">{t("common.loading") || "Caricamento…"}</p>
      ) : items.length === 0 ? (
        <div className="bg-white border border-stone-200 rounded-2xl p-10 text-center text-stone-400">
          {t("colleagues.empty")}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((d) => (
            <button key={d.id} data-testid={`colleague-${d.id}`}
              onClick={() => setActive(d)}
              className="text-left bg-white border border-stone-200 rounded-2xl p-5 hover:border-[#D4A373]/60 hover:shadow-sm transition-all">
              <div className="flex items-start gap-3">
                {d.photo_url ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${d.photo_url}`} alt=""
                    className="w-14 h-14 rounded-full object-cover border border-stone-200 flex-shrink-0" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-[#D4A373]/20 flex items-center justify-center flex-shrink-0">
                    <User className="w-6 h-6 text-[#B07D4F]" strokeWidth={1.5} />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-heading text-lg font-light truncate">{d.name}</div>
                  {d.specialization && (
                    <div className="flex items-center gap-1 text-xs text-stone-500 mt-0.5 truncate">
                      <Stethoscope className="w-3 h-3 flex-shrink-0" /> <span className="truncate">{d.specialization}</span>
                    </div>
                  )}
                  {(d.clinic_name || d.city) && (
                    <div className="flex items-center gap-1 text-xs text-stone-500 mt-0.5 truncate">
                      <MapPin className="w-3 h-3 flex-shrink-0" /> <span className="truncate">{[d.clinic_name, d.city].filter(Boolean).join(" · ")}</span>
                    </div>
                  )}
                </div>
              </div>
              {d.public_bio && (
                <p className="text-xs text-stone-500 leading-relaxed mt-3 line-clamp-3">{d.public_bio}</p>
              )}
              <div className="mt-3 pt-3 border-t border-stone-100 flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-widest text-[#B07D4F]">{t("colleagues.openProfile") || "Vedi profilo"}</span>
                <ExternalLink className="w-3 h-3 text-stone-400" />
              </div>
            </button>
          ))}
        </div>
      )}

      {active && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end md:items-center justify-center p-4"
          onClick={() => setActive(null)} data-testid="colleague-modal">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="relative">
              <div className="h-24 bg-gradient-to-br from-[#F5EFE6] to-[#D4A373]/40" />
              <button onClick={() => setActive(null)} data-testid="close-colleague-modal"
                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/80 backdrop-blur flex items-center justify-center hover:bg-white">
                <X className="w-4 h-4" />
              </button>
              <div className="px-6 -mt-10 flex items-end gap-4">
                {active.photo_url ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${active.photo_url}`} alt=""
                    className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-sm" />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-[#D4A373]/20 border-4 border-white flex items-center justify-center shadow-sm">
                    <User className="w-8 h-8 text-[#B07D4F]" strokeWidth={1.5} />
                  </div>
                )}
              </div>
            </div>
            <div className="px-6 pb-6 pt-4 space-y-4">
              <div>
                <div className="font-heading text-2xl font-light">{active.name}</div>
                {active.specialization && (
                  <div className="text-sm text-stone-600 mt-1 flex items-center gap-1.5">
                    <Stethoscope className="w-3.5 h-3.5" /> {active.specialization}
                  </div>
                )}
              </div>

              {active.public_bio && (
                <div className="bg-[#FAF9F6] border-l-2 border-[#D4A373] rounded-r-xl p-4">
                  <Quote className="w-4 h-4 text-[#D4A373] mb-2" strokeWidth={1.5} />
                  <p className="text-sm text-stone-700 leading-relaxed whitespace-pre-wrap">{active.public_bio}</p>
                </div>
              )}

              <div className="space-y-2 text-sm">
                {(active.clinic_name || active.clinic_address || active.city) && (
                  <div className="flex items-start gap-2 text-stone-600">
                    <MapPin className="w-3.5 h-3.5 text-stone-400 mt-0.5 flex-shrink-0" />
                    <div>
                      {active.clinic_name && <div className="font-medium text-stone-900">{active.clinic_name}</div>}
                      {(active.clinic_address || active.city) && (
                        <div className="text-xs text-stone-500">{[active.clinic_address, active.city].filter(Boolean).join(" · ")}</div>
                      )}
                    </div>
                  </div>
                )}
                {active.email && (
                  <a href={`mailto:${active.email}`} className="flex items-center gap-2 text-stone-600 hover:text-stone-900" data-testid="modal-email">
                    <Mail className="w-3.5 h-3.5 text-stone-400" /> {active.email}
                  </a>
                )}
                {active.phone && (
                  <a href={`tel:${active.phone}`} className="flex items-center gap-2 text-stone-600 hover:text-stone-900" data-testid="modal-phone">
                    <Phone className="w-3.5 h-3.5 text-stone-400" /> {active.phone}
                  </a>
                )}
              </div>

              {active.slug && (
                <a href={`/dr/${active.slug}`} target="_blank" rel="noreferrer"
                  data-testid="modal-open-portal"
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-full bg-stone-900 text-white text-xs uppercase tracking-widest hover:bg-stone-700">
                  <ExternalLink className="w-3.5 h-3.5" /> {t("colleagues.viewPortal")}
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
